import {useState, useEffect, useRef} from "react"
import {MediaImage} from "@/components/templates/media/media--image";
import {DrupalMedia} from "next-drupal";
import Link from "next/link";
import Slider from "react-slick";
import classNames from "classnames";

const LOGO_PLACEHOLDER_URL = `https://placehold.co/600x400?text=Logo`;
const IMAGE_PLACEHOLDER_URL = `https://placehold.co/600x400?text=Image`;
const CIRCLE_PLACEHOLDER_URL = `https://placehold.co/96x96?text=Image`;
const TESTIMONIAL_CARD_WIDTH = 350;
const TESTIMONIAL_DESCRIPTION_MAX_WORDS = 20;
const TESTIMONIAL_JOB_TITLE_MAX_WORDS = 6;

/** Strip HTML and truncate to a max word count. */
function truncateWords(value: string | undefined, maxWords: number): string {
  if (!value) return "";
  const plain = value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return "";
  const words = plain.split(" ");
  if (words.length <= maxWords) return plain;
  return `${words.slice(0, maxWords).join(" ")}…`;
}

interface SocialProofItem {
  id: string
  logo: DrupalMedia
  title?: string
  subtitle?: string
  description?: string
  caption?: string
  link?: string
}

interface SocialProofComponentProps {
  items: SocialProofItem[]
  layout: "logos-contained" | "logos-full-width" | "vertical-contained" | "horizontal-contained"
  isTestimonial?: boolean
  autoSlide?: boolean
  slideInterval?: number
}

export default function SocialProofComponent({
                                               items,
                                               layout,
                                               isTestimonial = false,
                                               autoSlide = true,
                                               slideInterval = 3000,
                                             }: SocialProofComponentProps) {
  const [currentIndex, setCurrentIndex] = useState(items.length) // Start from middle copy
  const [isPaused, setIsPaused] = useState(false)
  const sliderRef = useRef<HTMLDivElement>(null)

  // Determine if we need sliding based on layout and item count
  const needsSliding = () => {
    switch (layout) {
      case "logos-contained":
        return items.length > 4
      case "logos-full-width":
        return items.length > 6
      case "vertical-contained":
        return items.length > (isTestimonial ? 3 : 4)
      case "horizontal-contained":
        return items.length > 3
      default:
        return false
    }
  }

  const shouldSlide = needsSliding()

  // Get items per view based on layout
  const getItemsPerView = () => {
    switch (layout) {
      case "logos-contained":
        return 4
      case "logos-full-width":
        return 6
      case "vertical-contained":
        return isTestimonial ? 3 : 4
      case "horizontal-contained":
        return 3
      default:
        return items.length
    }
  }

  const itemsPerView = getItemsPerView()

  // Create extended array for infinite loop
  const getExtendedItems = () => {
    if (!shouldSlide) return items
    // Create 3 copies: [...items, ...items, ...items] for seamless looping
    return [...items, ...items, ...items]
  }

  const extendedItems = getExtendedItems()
  const startIndex = shouldSlide ? items.length : 0 // Start from middle copy

  // Auto slide functionality with pause on hover
  useEffect(() => {
    if (!shouldSlide || !autoSlide || isPaused) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev + 1)
    }, slideInterval)

    return () => clearInterval(interval)
  }, [shouldSlide, autoSlide, slideInterval, isPaused])

  // Reset to middle copy when reaching end of second copy (infinite loop)
  useEffect(() => {
    if (!shouldSlide) return

    // When we reach the end of the second copy, instantly reset to middle copy
    if (currentIndex >= items.length * 2) {
      // Wait just a tiny bit for the current transition to start, then reset instantly
      setTimeout(() => {
        setCurrentIndex(items.length)
      }, 50) // Very short delay to let transition start
    }
  }, [currentIndex, items.length, shouldSlide])

  const renderDots = () => {
    if (!shouldSlide) return null

    // Calculate which dot should be active - normalize to 0-based index
    const normalizedIndex = currentIndex - items.length
    const activeDotIndex = ((normalizedIndex % items.length) + items.length) % items.length

    return (
      <div className="flex justify-center space-x-2">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(items.length + index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeDotIndex === index ? "bg-gray-600" : "bg-gray-300"
            }`}
          />
        ))}
      </div>
    )
  }

  const renderLogosOnly = () => {
    const containerClass = layout === "logos-full-width" ? "component--social-proof w-full" : "component--social-proof max-w-[1200px] mx-auto"

    if (!shouldSlide) {
      return (
        <div className={containerClass}>
          <div
            className={
              layout === "logos-full-width"
                ? "flex justify-center items-center py-12"
                : "flex justify-start items-center py-12"
            }
            style={{gap: "48px"}}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {extendedItems.map((item, index) => (
              <div key={item.id} className="flex-shrink-0 text-center" style={{width: "266px"}}>
                {(item?.link && item?.link?.length > 0) ? (
                  <Link href={item.link} target="_blank">
                    <MediaImage
                      media={item.logo}
                    />
                  </Link>
                ) : (
                  <MediaImage
                    media={item.logo}
                  />
                )}
                {item.caption && (
                  <div className="[&_p]:text-sm [&_p]:text-gray-600 mt-2 [&_p]:font-medium"
                       dangerouslySetInnerHTML={{__html: item.caption}}/>
                )}
              </div>
            ))}
          </div>
        </div>
      )
    }

    const itemWidth = 266 + 48 // 266px logo + 48px gap
    const totalWidth = extendedItems.length * itemWidth
    const containerWidth =
      layout === "logos-full-width" ? (typeof window !== "undefined" ? window.innerWidth : 1200) : 1200
    const visibleWidth = itemsPerView * itemWidth - 48 // Remove last gap
    const centerOffset = layout === "logos-full-width" ? (containerWidth - visibleWidth) / 2 : 0
    const initialOffset = startIndex * itemWidth - centerOffset

    const slickSettings = {
      arrows: false,
      autoplay: true,
      autoplaySpeed: 2000,
      speed: 500,
      dots: true,
      infinite: true,
      slide: 'div',
      cssEase: 'linear',
      pauseOnHover: true,
      slidesToShow: layout === "logos-full-width" ? 7 : 4,
      slidesToScroll: 1,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1,
          }
        }
      ]
    };

    return (
      <>
      <div className={containerClass}>
        <Slider {...slickSettings}>
          {items.map((item, index) => (
            <div key={item.id} className="flex-shrink-0 text-center !flex flex-col items-center justify-center h-full" style={{width: "266px"}}>
              {(item?.link && item?.link?.length > 0) ? (
                <Link href={item.link} target="_blank">
                  <MediaImage
                    media={item.logo}
                  />
                </Link>
              ) : (
                <MediaImage
                  media={item.logo}
                  className="max-w-[200px]"
                />
              )}
              {item.caption && (
                <div className="[&_p]:text-sm [&_p]:text-gray-600 mt-2 [&_p]:font-medium"
                     dangerouslySetInnerHTML={{__html: item.caption}}/>
              )}
            </div>
          ))}
        </Slider>
      </div>
      </>
    )
  }

  const renderVerticalCards = () => {
    const slickSettings = {
      arrows: false,
      autoplay: true,
      autoplaySpeed: 2000,
      speed: 500,
      dots: items?.length > 4,
      infinite: items?.length > 4,
      variableWidth: isTestimonial,
      centerMode: isTestimonial && items?.length < 3,
      slide: 'div',
      cssEase: 'linear',
      pauseOnHover: true,
      slidesToShow: isTestimonial ? 3 : 4,
      slidesToScroll: 1,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1,
            dots: true,
            infinite: true,
            variableWidth: false,
          }
        }
      ]
    };

    const renderTestimonialImage = (item: SocialProofItem) => {
      const image = (
        <MediaImage
          media={item.logo}
          fill
          sizes="96px"
          childClass="object-cover rounded-full"
          className="relative !w-24 !h-24 mx-auto overflow-hidden rounded-full"
          style={{objectFit: "cover"}}
        />
      );

      if (item?.link && item?.link?.length > 0) {
        return (
          <Link href={item.link} target="_blank" className="block w-24 h-24 mx-auto">
            {image}
          </Link>
        );
      }

      return image;
    };

    return (
      <div className="component--social-proof vertical-cards max-w-[1200px] mx-auto">
        <Slider {...slickSettings}>
          {items.map((item) => {
            const jobTitle = isTestimonial
              ? truncateWords(item.subtitle, TESTIMONIAL_JOB_TITLE_MAX_WORDS)
              : item.subtitle;
            const description = isTestimonial
              ? truncateWords(item.description, TESTIMONIAL_DESCRIPTION_MAX_WORDS)
              : item.description;

            return (
              <div
                key={item.id}
                className={classNames(
                  "bg-white rounded-lg shadow-sm border border-gray-100 text-center flex flex-col overflow-hidden h-full",
                  {
                    "p-8": isTestimonial,
                  },
                )}
                style={isTestimonial ? {width: TESTIMONIAL_CARD_WIDTH, maxWidth: "100%"} : undefined}
              >
                <div className={`flex-shrink-0 max-w-full mx-auto ${isTestimonial ? "mb-4" : "-m-6 mb-4"}`}>
                  {isTestimonial ? (
                    renderTestimonialImage(item)
                  ) : (
                    <>
                      {item?.link && item?.link?.length > 0 ? (
                        <Link href={item.link} target="_blank">
                          <MediaImage
                            media={item.logo}
                            style={{height: "280px", width: "100%", objectFit: "cover"}}
                            className="w-full h-15 object-cover rounded-t-lg ok"
                          />
                        </Link>
                      ) : (
                        <MediaImage
                          media={item.logo}
                          style={{height: "280px", width: "100%", objectFit: "cover"}}
                          className="w-full h-15 object-cover rounded-t-lg ok"
                        />
                      )}
                    </>
                  )}
                </div>
                <div className={`flex-grow flex flex-col ${isTestimonial ? "" : "px-6"}`}>
                  {item.title && (
                    <h3
                      className={`font-bold mb-3 p-0 [&_p]:m-0 [&_p]:p-0 [&_p]:text-2xl ${isTestimonial ? "text-sm [&_p]:text-sm text-black" : "text-2xl [&_p]:text-primary"}`}
                      style={isTestimonial ? {} : {color: "#00b0f0"}}
                      dangerouslySetInnerHTML={{__html: item.title}}
                    />
                  )}
                  {isTestimonial && jobTitle && (
                    <p className="text-sm italic text-gray-600 mb-3">{jobTitle}</p>
                  )}
                  {description && (
                    isTestimonial ? (
                      <p className="text-base text-gray-600 leading-relaxed flex-grow">{description}</p>
                    ) : (
                      <div
                        className="text-base text-gray-600 leading-relaxed [&_p]:text-base"
                        dangerouslySetInnerHTML={{__html: description}}
                      />
                    )
                  )}
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    )
  }

  const renderHorizontalCards = () => {
    if (!shouldSlide) {
      return (
        <div
          className="max-w-[1200px] mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="flex justify-start items-stretch" style={{gap: "24px"}}>
            {extendedItems.map((item, index) => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex"
                style={{width: "376px"}}
              >
                <div className={`flex-shrink-0 mr-4 ${isTestimonial ? "self-start" : "self-center"}`}>
                  <img
                    src={`${LOGO_PLACEHOLDER_URL} ${index + 1}`}
                    alt={`Logo ${index + 1}`}
                    className="w-16 h-16 object-contain rounded-full"
                  />
                </div>
                <div className="flex-grow">
                  {item.title && (
                    <h3
                      className={`font-bold mb-2 ${isTestimonial ? "text-sm text-black" : "text-2xl"}`}
                      style={isTestimonial ? {} : {color: "#00b0f0"}}
                    >
                      {item.title.length > 15 ? `${item.title.substring(0, 15)}...` : item.title}
                    </h3>
                  )}
                  {isTestimonial && item.subtitle && (
                    <p className="text-sm italic text-gray-600 mb-3">{item.subtitle}</p>
                  )}
                  {item.description && (
                    <p className="text-base text-gray-600 leading-relaxed">
                      {item.description.length > 130 ? `${item.description.substring(0, 130)}...` : item.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    const cardWidth = 376 + 24 // 376px card + 24px gap
    const totalWidth = extendedItems.length * cardWidth
    const visibleWidth = itemsPerView * cardWidth - 24 // Remove last gap
    const centerOffset = (1200 - visibleWidth) / 2
    const initialOffset = startIndex * cardWidth - centerOffset

    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="overflow-hidden" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <div
            ref={sliderRef}
            className="flex items-stretch transition-transform duration-700 ease-in-out"
            style={{
              transform: `translateX(-${initialOffset + (currentIndex - startIndex) * cardWidth}px)`,
              width: `${totalWidth}px`,
              transition: currentIndex === items.length ? "none" : "transform 700ms ease-in-out",
              gap: "24px",
            }}
          >
            {extendedItems.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-shrink-0"
                style={{width: "376px"}}
              >
                <div className={`flex-shrink-0 mr-4 ${isTestimonial ? "self-start" : "self-center"}`}>
                  <img
                    src={`${LOGO_PLACEHOLDER_URL} ${index + 1}`}
                    alt={`Logo ${(index % items.length) + 1}`}
                    className="w-16 h-16 object-contain rounded-full"
                  />
                </div>
                <div className="flex-grow">
                  {item.title && (
                    <h3
                      className={`font-bold mb-2 ${isTestimonial ? "text-sm text-black" : "text-2xl"}`}
                      style={isTestimonial ? {} : {color: "#00b0f0"}}
                    >
                      {item.title.length > 15 ? `${item.title.substring(0, 15)}...` : item.title}
                    </h3>
                  )}
                  {isTestimonial && item.subtitle && (
                    <p className="text-sm italic text-gray-600 mb-3">{item.subtitle}</p>
                  )}
                  {item.description && (
                    <p className="text-base text-gray-600 leading-relaxed">
                      {item.description.length > 130 ? `${item.description.substring(0, 130)}...` : item.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {renderDots()}
      </div>
    )
  }

  const renderContent = () => {
    switch (layout) {
      case "logos-contained":
      case "logos-full-width":
        return renderLogosOnly()
      case "vertical-contained":
        return renderVerticalCards()
      case "horizontal-contained":
        return renderHorizontalCards()
      default:
        return null
    }
  }

  return <div className="pb-12">{renderContent()}</div>
}
